import { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LogoutIcon from '@mui/icons-material/Logout';
import { toggleSidebar, toggleSidebarCollapsed } from '@/redux/slices/uiSlice';
import { ColorModeContext } from '@/contexts/ColorModeContext';
import useAuth from '@/hooks/useAuth';
import { ROLE_LABELS } from '@/constants/roles';
import { ROUTES } from '@/routes/paths';
import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from './Sidebar';

export default function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const colorMode = useContext(ColorModeContext);
  const sidebarCollapsed = useSelector((s) => s.ui.sidebarCollapsed);
  const navWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;
  const [anchor, setAnchor] = useState(null);

  const handleLogout = async () => {
    setAnchor(null);
    await logout();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { md: `calc(100% - ${navWidth}px)` },
        ml: { md: `${navWidth}px` },
        backdropFilter: 'blur(6px)',
        bgcolor: 'background.paper',
        borderBottom: '1px solid rgba(145,158,171,0.16)',
        transition: (t) => t.transitions.create(['width', 'margin'], { duration: t.transitions.duration.enteringScreen }),
      }}
    >
      <Toolbar>
        <IconButton edge="start" onClick={() => dispatch(toggleSidebar())} sx={{ mr: 1, display: { md: 'none' } }}>
          <MenuIcon />
        </IconButton>
        <IconButton
          edge="start"
          onClick={() => dispatch(toggleSidebarCollapsed())}
          sx={{ mr: 1, display: { xs: 'none', md: 'inline-flex' } }}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
        </IconButton>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={colorMode.toggle} aria-label="toggle theme">
          {colorMode.mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
        </IconButton>
        <Box sx={{ ml: 1 }}>
          <IconButton onClick={(e) => setAnchor(e.currentTarget)} size="small">
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
              {(admin?.name || 'A')[0].toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">{admin?.name || 'Admin'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {admin?.email}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip size="small" label={ROLE_LABELS[admin?.role] || admin?.role} color="primary" variant="outlined" />
              </Box>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
